import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { isDemoMode } from "@/lib/demo-mode";
import { tenantApi } from "./tenant.api";
import {
  getMockTenant,
  getMockCampuses,
  getMockCampus,
  getMockSubscription,
  getMockSubscriptionHistory,
  getMockSettings,
  MOCK_CAMPUSES,
} from "./tenant.mock";
import type {
  CampusListParams,
  UpdateTenantProfileDTO,
  CreateCampusDTO,
  UpdateCampusDTO,
  UpdateSubscriptionDTO,
  UpsertSettingsDTO,
} from "../types/tenant.types";

// ── Query keys ─────────────────────────────────────────────────────────────────

export const tenantKeys = {
  profile:             ["tenant", "profile"] as const,
  campuses:            (params?: CampusListParams) => ["tenant", "campuses", params] as const,
  campusesAll:         ["tenant", "campuses"] as const,
  campus:              (id: string) => ["tenant", "campus", id] as const,
  subscription:        ["tenant", "subscription"] as const,
  subscriptionHistory: (page: number) => ["tenant", "subscription", "history", page] as const,
  settings:            ["tenant", "settings"] as const,
};

// ── Queries ────────────────────────────────────────────────────────────────────

// Normalise PascalCase backend response → snake_case Tenant shape.
// Remove once the backend Tenant struct has json tags deployed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeTenant(raw: any) {
  if (!raw) return null;
  // Already snake_case (post-deployment)
  if (raw.id) return raw;
  // PascalCase fallback
  return {
    id:                  raw.ID,
    name:                raw.Name,
    slug:                raw.Slug,
    school_type:         raw.SchoolType,
    curriculum_system:   raw.CurriculumSystem,
    term_structure:      raw.TermStructure,
    country:             raw.Country,
    timezone:            raw.Timezone,
    default_language:    raw.DefaultLanguage,
    currency:            raw.Currency,
    phone:               raw.Phone,
    email:               raw.Email,
    website:             raw.Website,
    address_line1:       raw.AddressLine1,
    address_line2:       raw.AddressLine2,
    city:                raw.City,
    state_province:      raw.StateProvince,
    postal_code:         raw.PostalCode,
    logo_url:            raw.LogoURL,
    motto:               raw.Motto,
    established_date:    raw.EstablishedDate,
    registration_number: raw.RegistrationNumber,
    subscription_tier:   raw.SubscriptionTier,
    max_students:        raw.MaxStudents,
    status:              raw.Status,
    settings:            raw.Settings,
    data_region:         raw.DataRegion,
    trial_ends_at:       raw.TrialEndsAt,
    version:             raw.Version,
    created_at:          raw.CreatedAt,
    updated_at:          raw.UpdatedAt,
  };
}

export function useTenant() {
  return useQuery({
    queryKey: tenantKeys.profile,
    queryFn: async () => {
      if (isDemoMode()) return getMockTenant();
      const raw = await tenantApi.get();
      return normalizeTenant(raw);
    },
    staleTime: 60_000,
  });
}

export function useCampuses(params?: CampusListParams) {
  return useQuery({
    queryKey: tenantKeys.campuses(params),
    queryFn: async () => {
      if (isDemoMode()) return getMockCampuses(params);
      return tenantApi.getCampuses(params);
    },
    staleTime: 60_000,
  });
}

export function useCampus(id: string) {
  return useQuery({
    queryKey: tenantKeys.campus(id),
    queryFn: async () => {
      if (isDemoMode()) return getMockCampus(id) ?? MOCK_CAMPUSES[0];
      return tenantApi.getCampus(id);
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: tenantKeys.subscription,
    queryFn: async () => {
      if (isDemoMode()) return getMockSubscription();
      return tenantApi.getSubscription();
    },
    staleTime: 120_000,
  });
}

export function useSubscriptionHistory(page = 1) {
  return useQuery({
    queryKey: tenantKeys.subscriptionHistory(page),
    queryFn: async () => {
      if (isDemoMode()) return getMockSubscriptionHistory({ page, page_size: 10 });
      return tenantApi.getSubscriptionHistory({ page, page_size: 10 });
    },
    staleTime: 120_000,
  });
}

export function useTenantSettings() {
  return useQuery({
    queryKey: tenantKeys.settings,
    queryFn: async () => {
      if (isDemoMode()) return getMockSettings({ page: 1, page_size: 100 });
      return tenantApi.getSettings({ page: 1, page_size: 100 });
    },
    staleTime: 120_000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTenantProfileDTO) => tenantApi.update(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(tenantKeys.profile, updated);
    },
  });
}

export function useCreateCampus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCampusDTO) => tenantApi.createCampus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.campusesAll });
      queryClient.invalidateQueries({ queryKey: tenantKeys.profile });
    },
  });
}

export function useUpdateCampus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCampusDTO) => tenantApi.updateCampus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.campusesAll });
      queryClient.invalidateQueries({ queryKey: tenantKeys.campus(id) });
    },
  });
}

export function useDeleteCampus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantApi.deleteCampus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.campusesAll });
      queryClient.invalidateQueries({ queryKey: tenantKeys.profile });
    },
  });
}

export function useSetPrimaryCampus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantApi.setPrimaryCampus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.campusesAll });
    },
  });
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSubscriptionDTO) => tenantApi.updateSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.subscription });
      queryClient.invalidateQueries({ queryKey: tenantKeys.subscriptionHistory(1) });
    },
  });
}

export function useUpsertSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertSettingsDTO) => tenantApi.upsertSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.settings });
    },
  });
}

// ── Self-service (usage, logo, export, settings reset, deactivation) ─────────

export function useTenantUsage() {
  return useQuery({
    queryKey: ["tenant", "usage"] as const,
    queryFn: () => tenantApi.getUsage(),
    staleTime: 60_000,
    enabled: !isDemoMode(),
  });
}

export function useSettingsHistory(page = 1) {
  return useQuery({
    queryKey: ["tenant", "settings", "history", page] as const,
    queryFn: () => tenantApi.getSettingsHistory({ page, page_size: 20 }),
    enabled: !isDemoMode(),
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => tenantApi.uploadLogo(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantKeys.profile }),
  });
}

export function useResetSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tenantApi.resetSettings(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantKeys.settings }),
  });
}

export function useStartExport() {
  return useMutation({
    mutationFn: () => tenantApi.startExport(),
  });
}

export function useExportStatus(id: string | null) {
  return useQuery({
    queryKey: ["tenant", "export", id] as const,
    queryFn: () => tenantApi.getExport(id as string),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && status !== "completed" && status !== "failed" ? 3000 : false;
    },
  });
}

export function useDeactivationStatus() {
  return useQuery({
    queryKey: ["tenant", "deactivation"] as const,
    queryFn: () => tenantApi.getDeactivationStatus(),
    enabled: !isDemoMode(),
  });
}

export function useRequestDeactivation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => tenantApi.requestDeactivation(reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tenant", "deactivation"] }),
  });
}

export function useCancelDeactivation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tenantApi.cancelDeactivation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tenant", "deactivation"] }),
  });
}
